import type { DateTime } from 'luxon'

import { isNotNull, wordWithArticle } from '@/utils/helpers'

import SessionAttendeeQ from '@/queries/SessionAttendeeQ'
import SessionQ from '@/queries/SessionQ'
import WorldMemberQ from '@/queries/WorldMemberQ'
import WorldQ from '@/queries/WorldQ'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { useServerNow } from '@/client/hooks'
import { SQL } from '@/client/query'
import { getTutorOfferingPageUrl } from '@/client/services/url'
import {
  ALGEBRA_2_WORLD_SLUG,
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  CALCULUS_WORLD_SLUG,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  COMMUNITY_WORLD_SLUGS,
  GEOMETRY_WORLD_SLUG,
  getNextLevel,
  getWorldLevelTitle,
  isLevelAtLeastMinLevel,
  isProgramWorld,
  LevelRequirementStatus,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
  PRE_CALCULUS_WORLD_SLUG,
  PROGRAM_SUBWORLD_SLUGS,
  SAT_BOOTCAMP_WORLD_SLUG,
  STATISTICS_WORLD_SLUG,
  TEST_PREP_WORLD_SLUGS,
} from '@/client/services/worlds'

import useAllProgramIterationsOpenForRegistration from '@/components/Programs/hooks/useAllProgramIterationsOpenForRegistration'
import useMemberWorldsWithNextStep from '@/components/Worlds/hooks/world/useMemberWorldsWithNextStep'
import { findCurrentStepIndex } from '@/components/Worlds/Journey/helpers'

import { Priority, type TodoItem } from '../types'

const ATTENDANCE_PRIORITY_MIN = 3

const HIGH_PRIORITY_WORLD_SLUGS = [
  ALGEBRA_2_WORLD_SLUG,
  GEOMETRY_WORLD_SLUG,
  PRE_CALCULUS_WORLD_SLUG,
  CALCULUS_WORLD_SLUG,
  STATISTICS_WORLD_SLUG,
  ...TEST_PREP_WORLD_SLUGS,
  COLLEGE_ADMISSIONS_WORKSHOPS_WORLD_SLUG,
  ALGEBRA2_LEARNING_LABS_WORLD_SLUG,
  PRE_CALCULUS_LEARNING_LABS_WORLD_SLUG,
]

export function useNextStepTodos({ worldSlugs }: { worldSlugs: string[] }): TodoItem[] {
  const currentUser = useUser()
  const query = useQuery()
  const now = useServerNow()

  const programsOpenForTutorRegistration = useAllProgramIterationsOpenForRegistration({
    type: 'tutor',
  })
  const worldSlugToTutorDeadline: Record<string, DateTime | null> = Object.fromEntries(
    Object.entries(programsOpenForTutorRegistration).map(([worldSlug, iteration]) => [
      worldSlug,
      iteration?.tutorRegistrationDeadline.minus({ seconds: 1 }) ?? null,
    ])
  )

  const nextSteps = useMemberWorldsWithNextStep(['slug', 'title'], {
    // We do not show Journey to-dos for community subworlds
    worldSlugsToInclude: worldSlugs.filter(slug => !COMMUNITY_WORLD_SLUGS.includes(slug)),
  })

  const isSATTutor = currentUser.satBootcampStates.some(s => s.type === 'tutor')

  const isTutorInHighPriorityWorld = Object.values(currentUser.worlds).some(
    world =>
      HIGH_PRIORITY_WORLD_SLUGS.includes(world.slug) &&
      isLevelAtLeastMinLevel({ level: world.level, minLevel: 'tutor' })
  )

  // For program worlds, we only want to show tutor todos for members who have expressed intent to tutor.
  const programExpressedIntentToTutor: Record<string, boolean> = Object.fromEntries(
    PROGRAM_SUBWORLD_SLUGS.map(worldSlug => {
      const worldMember = query.one(
        WorldMemberQ.where({
          userId: currentUser.id,
          include: [
            {
              association: 'World',
              where: SQL`${WorldQ.slug} = ${worldSlug}`,
            },
          ],
        })
      )
      const expressedIntentToTutor = worldMember(WorldMemberQ.expressedIntentToTutor()) ?? false
      // Sometimes we don't have people's help intents. We still want to show next step todos for these tutors to become senior tutors!
      const isTutor = worldMember(WorldMemberQ.isAtLeastLevel('tutor')) ?? false

      return [worldSlug, expressedIntentToTutor || isTutor]
    })
  )

  // prioritizedWorldSlugs is trying to guess where someone intends to be a tutor (i.e. intent to give help, or is currently a contributor). It also has "Attended X sessions" as a potential way to convert learners into tutors. Made the assumption that if something isn't in that list, then they're probably just learning / exploring, and that we should prioritize the journey to-dos below the learning to-dos for those subworlds
  const prioritizedWorldSlugs = query
    .many(
      WorldMemberQ.where(
        {
          userId: currentUser.id,
          include: [
            {
              association: 'World',
              where: WorldQ.hasAnySlug(worldSlugs),
            },
          ],
        },
        SQL`(${WorldMemberQ.hasHelpIntent('give-help')}
          OR (SELECT COUNT(DISTINCT ${SessionAttendeeQ.session_id}) >= ${ATTENDANCE_PRIORITY_MIN}
            ${SessionAttendeeQ.where({
              userId: currentUser.id,
              include: [
                {
                  association: 'Session',
                  required: true,
                  where: SQL`${SessionQ.world_id} = ${WorldMemberQ.world_id}`,
                },
              ],
            })}
          )
        )`
      )
    )
    .map(worldMember => worldMember(WorldQ.slug) ?? '')

  const nextStepTodos: TodoItem[] = nextSteps
    .map(world => {
      const { nextLevelSteps, currentLevel } = world

      if (COMMUNITY_WORLD_SLUGS.includes(world.slug)) {
        return null
      }

      // For programs, we don't want to show "Become a tutor" unless a user has explicitly expressed this intent. We want learners to see learner actions because they are unlikely to convert to tutors.
      if (
        world.slug in programExpressedIntentToTutor &&
        programExpressedIntentToTutor[world.slug] === false
      ) {
        return null
      }

      const numIncompleteSteps = nextLevelSteps.filter(
        step =>
          ![LevelRequirementStatus.Complete, LevelRequirementStatus.NotApplicable].includes(
            step.status
          )
      ).length

      const nextStepIndex = findCurrentStepIndex(nextLevelSteps)
      const nextStep = nextStepIndex == null ? null : nextLevelSteps[nextStepIndex]

      const nextLevel = getNextLevel({ currentLevel, worldSlug: world.slug })
      const levelBeingWorkedOn = nextLevelSteps.length === 0 ? null : nextLevel

      if (nextStep == null || levelBeingWorkedOn == null) {
        return null
      }

      // TODO: Bring this back after we've ironed out the steps to become a coach.
      if (levelBeingWorkedOn === 'coach') {
        return null
      }

      let priority = Priority.Chill
      if (prioritizedWorldSlugs.includes(world.slug)) {
        // If you aren't a tutor anywhere yet, then prioritize being a tutor in something that we consider higher priority (i.e. calculus and test prep rather than R&W and history)
        if (HIGH_PRIORITY_WORLD_SLUGS.includes(world.slug)) {
          if (levelBeingWorkedOn === 'tutor' && !isTutorInHighPriorityWorld) {
            priority = Priority.High
          } else {
            priority = Priority.Medium
          }
        }
        if (world.slug === SAT_BOOTCAMP_WORLD_SLUG && !isSATTutor) {
          priority = Priority.Chill
        }
      }

      const levelTitle = getWorldLevelTitle({
        worldSlug: world.slug,
        level: levelBeingWorkedOn,
      })
      const becomeTitle =
        (nextStepIndex ?? 0) > 0 && nextLevel === 'tutor'
          ? `Finish becoming ${wordWithArticle(levelTitle)}`
          : `Become ${wordWithArticle(levelTitle)}`

      const tutorOfferingPageUrl = isProgramWorld(world.slug)
        ? getTutorOfferingPageUrl({ slug: world.slug, type: 'program' })
        : getTutorOfferingPageUrl({ slug: world.slug, type: 'subject' })

      return {
        worldSlug: world.slug,
        title: becomeTitle,
        stepsRemaining: numIncompleteSteps,
        subtitle: `Next Up: ${nextStep.requirement.title}`,
        completed: false,
        link: tutorOfferingPageUrl,
        emoji: '📈',
        deadline:
          levelBeingWorkedOn === 'tutor' && world.slug in worldSlugToTutorDeadline
            ? worldSlugToTutorDeadline[world.slug]
            : null,
        priority,
      }
    })
    .filter(isNotNull)
    .filter(todo => todo.deadline == null || todo.deadline > now)

  return nextStepTodos
}
